<script setup lang="ts">
import { ref, shallowRef, computed, watch, onMounted, nextTick } from 'vue';
import type { Component, ShallowRef } from 'vue';
import { toIsoLocal, formatDMY } from '~/utils/iso-date';
import { getMaxBookableDate } from '~~/utils/booking-window';

type Theme = 'hero-glass' | 'search-hero';

const props = withDefaults(
  defineProps<{
    theme: Theme;
    initialFrom?: string;
    initialTo?: string;
    initialGuests?: number;
  }>(),
  {
    initialFrom: undefined,
    initialTo: undefined,
    initialGuests: undefined,
  },
);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { trackEvent } = useAnalytics();

const parseIso = (s?: string | null): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const readRouteIso = (key: 'from' | 'to'): string => {
  const v = route.query[key];
  return typeof v === 'string' ? v : '';
};

const minDate = ref<Date | null>(null);
const maxDate = ref<Date | null>(null);
const runtimeConfig = useRuntimeConfig();
const arriveDate = ref<Date | null>(
  parseIso(props.initialFrom ?? readRouteIso('from')),
);
const departDate = ref<Date | null>(
  parseIso(props.initialTo ?? readRouteIso('to')),
);
const peopleCount = ref<number>(
  Number(props.initialGuests ?? route.query.guests ?? 2) || 2,
);

const departMinDate = computed(() => {
  if (!arriveDate.value) return minDate.value ?? undefined;
  const next = new Date(arriveDate.value.getTime());
  next.setDate(next.getDate() + 1);
  return next;
});

// Clear depart if it becomes invalid after arrive change
watch(arriveDate, (v) => {
  if (v && departDate.value && departDate.value <= v) departDate.value = null;
});

// Sync local state when route.query changes externally (back/forward, clear)
watch(
  () => route.query,
  (q) => {
    const newFrom = parseIso(typeof q.from === 'string' ? q.from : '');
    const newTo = parseIso(typeof q.to === 'string' ? q.to : '');
    const newGuests = Number(q.guests ?? 2) || 2;
    if ((newFrom?.getTime() ?? 0) !== (arriveDate.value?.getTime() ?? 0)) {
      arriveDate.value = newFrom;
    }
    if ((newTo?.getTime() ?? 0) !== (departDate.value?.getTime() ?? 0)) {
      departDate.value = newTo;
    }
    if (newGuests !== peopleCount.value) {
      peopleCount.value = newGuests;
    }
  },
);

// On the home hero, mirror form state into the URL so apartment cards under
// the form (ApartmentCard) can pick the dates up via `route.query` and pass
// them along when navigating to /apartment/[id]. Without this, dates entered
// in the home hero are lost the moment the user clicks a card instead of
// the "Search" button. Scoped to `hero-glass` so /search behavior — where
// the URL is updated only on submit — stays unchanged. The reverse-sync
// watch above guards against echo: route.query → state is value-equality
// gated, so router.replace doesn't loop back.
if (props.theme === 'hero-glass') {
  watch(
    [arriveDate, departDate, peopleCount],
    ([a, d, g]) => {
      if (!import.meta.client) return;
      // Spread to preserve unrelated params on the home URL — UTMs, refs etc.
      const next = { ...route.query };
      if (a) next.from = toIsoLocal(a);
      else delete next.from;
      if (d) next.to = toIsoLocal(d);
      else delete next.to;
      // Omit `guests` from the URL when nothing else is set and value is the
      // default (2) — keeps the bare home URL clean for shares.
      if (g && (a || d || g !== 2)) next.guests = String(g);
      else delete next.guests;
      router.replace({ query: next });
    },
  );
}

onMounted(() => {
  const now = new Date();
  minDate.value = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  maxDate.value = getMaxBookableDate(Number(runtimeConfig.public.bookingWindowDays), now);
});

const formatDate = (d: Date | Date[]) => {
  const date = Array.isArray(d) ? d[0] : d;
  return date ? formatDMY(toIsoLocal(date)) : '';
};

const canSubmit = computed(
  () => !!(arriveDate.value && departDate.value && peopleCount.value),
);

const handleSearch = () => {
  if (!canSubmit.value) return;
  const from = toIsoLocal(arriveDate.value!);
  const to = toIsoLocal(departDate.value!);
  if (from >= to) return;
  // Dual-emission: keep back-compat event for GA4 historical dashboards while
  // the new unified `search_submit` event rolls out. Remove `hero_search_submit`
  // ~3 months post-ship in a separate commit.
  if (props.theme === 'hero-glass') {
    trackEvent('hero_search_submit', { from, to, guests: peopleCount.value });
  }
  trackEvent('search_submit', {
    placement: props.theme,
    from,
    to,
    guests: peopleCount.value,
  });
  navigateTo({
    path: localePath('/search'),
    query: { from, to, guests: peopleCount.value },
  });
};

// When the user clears either check-in or check-out via the VueDatePicker
// clear button, drop the other date and — on the /search page — reset the URL
// so the results grid falls back to the catalog (no-params) view.
const handleDateCleared = () => {
  arriveDate.value = null;
  departDate.value = null;
  if (
    props.theme === 'search-hero' &&
    (route.query.from || route.query.to || route.query.guests)
  ) {
    navigateTo({ path: localePath('/search') });
  }
};

const containerClass = computed(() =>
  props.theme === 'hero-glass'
    ? 'p-4 sm:p-6 bg-white/5 dark:bg-slate-900/15 backdrop-blur-md border border-white/10 rounded-2xl shadow-lg'
    : 'p-4 sm:p-6 bg-slate-900/20 border border-slate-700/40 backdrop-blur-sm rounded-xl shadow-lg',
);

const labelClasses =
  'block font-medium text-white/90 text-sm uppercase tracking-wide';
const iconClasses =
  'size-[18px] absolute top-1/2 -translate-y-1/2 start-4 text-white/70 pointer-events-none';
const inputClasses =
  'w-full py-3 px-4 ps-11 h-12 bg-white/10 text-white placeholder-white/50 rounded-lg outline-none border border-white/20 focus:bg-white/20 focus:border-white/40 transition-all duration-300 backdrop-blur-sm';

// Async-loaded VueDatePicker. Until first user interaction with a date
// field, render a skeleton button with the EXACT same .dp__input geometry
// (height, padding, border, font-size, background). On focus/pointerdown
// of the skeleton, load JS+CSS in parallel and swap inplace, then
// programmatically open the picker so the user's click flow continues
// without a second tap. History: vcalendar.client.ts plugin was deleted
// 2026-05-26; a previous attempt with plain defineAsyncComponent
// (2026-05-02) regressed LCP from 1.6s → NO_LCP because late hydration
// shifted hero layout. This approach preserves layout because the
// skeleton button reserves the exact pixel footprint of .dp__input.
type DatepickerInstance = { openMenu?: () => void };
// Cast: shallowRef's conditional return type distributes over the Component
// union, producing a union of ShallowRefs whose .value is write-locked.
const DatePicker = shallowRef(null) as ShallowRef<Component | null>;
const arriveDp = ref<DatepickerInstance | null>(null);
const departDp = ref<DatepickerInstance | null>(null);
const loadingDp = ref(false);

async function loadDatePicker(target: 'arrive' | 'depart') {
  if (DatePicker.value || loadingDp.value) return;
  loadingDp.value = true;
  try {
    const [mod] = await Promise.all([
      import('@vuepic/vue-datepicker'),
      import('@vuepic/vue-datepicker/dist/main.css'),
    ]);
    DatePicker.value = mod.VueDatePicker as unknown as Component;
    await nextTick();
    const dp = target === 'arrive' ? arriveDp.value : departDp.value;
    dp?.openMenu?.();
  } finally {
    loadingDp.value = false;
  }
}
</script>

<template>
  <div :class="containerClass">
    <div class="text-start">
      <div
        class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-3 md:gap-5 items-end"
      >
        <!-- Check-in -->
        <div>
          <label :class="labelClasses" for="booking-arrive">
            {{ t('roomselector.selectDateArrive') }}:
          </label>
          <div class="mt-2 booking-datepicker">
            <button
              v-if="!DatePicker"
              id="booking-arrive"
              type="button"
              class="dp__input dp__skeleton"
              :class="{ 'dp__skeleton--loading': loadingDp }"
              :aria-label="t('roomselector.selectDateArrive')"
              @pointerdown="loadDatePicker('arrive')"
              @focus="loadDatePicker('arrive')"
            >
              <Icon name="lucide:calendar" class="dp__skeleton__icon" aria-hidden="true" />
              <span class="dp__skeleton__text" :class="{ 'is-placeholder': !arriveDate }">
                {{ arriveDate ? formatDate(arriveDate) : t('roomselector.selectDate') }}
              </span>
            </button>
            <component
              :is="DatePicker"
              v-else
              id="booking-arrive"
              ref="arriveDp"
              v-model="arriveDate"
              :min-date="minDate"
              :max-date="maxDate"
              :time-config="{ enableTimePicker: false }"
              :formats="{ input: formatDate }"
              teleport="body"
              auto-apply
              :placeholder="t('roomselector.selectDate')"
              clearable
              @cleared="handleDateCleared"
            />
          </div>
        </div>

        <!-- Check-out -->
        <div>
          <label :class="labelClasses" for="booking-depart">
            {{ t('roomselector.selectDateDepart') }}:
          </label>
          <div class="mt-2 booking-datepicker">
            <button
              v-if="!DatePicker"
              id="booking-depart"
              type="button"
              class="dp__input dp__skeleton"
              :class="{ 'dp__skeleton--loading': loadingDp }"
              :aria-label="t('roomselector.selectDateDepart')"
              @pointerdown="loadDatePicker('depart')"
              @focus="loadDatePicker('depart')"
            >
              <Icon name="lucide:calendar" class="dp__skeleton__icon" aria-hidden="true" />
              <span class="dp__skeleton__text" :class="{ 'is-placeholder': !departDate }">
                {{ departDate ? formatDate(departDate) : t('roomselector.selectDate') }}
              </span>
            </button>
            <component
              :is="DatePicker"
              v-else
              id="booking-depart"
              ref="departDp"
              v-model="departDate"
              :min-date="departMinDate"
              :max-date="maxDate"
              :start-date="arriveDate ?? undefined"
              :time-config="{ enableTimePicker: false }"
              :formats="{ input: formatDate }"
              teleport="body"
              auto-apply
              :placeholder="t('roomselector.selectDate')"
              clearable
              @cleared="handleDateCleared"
            />
          </div>
        </div>

        <!-- Guests -->
        <div>
          <label :class="labelClasses" for="booking-guests">
            {{ t('roomselector.selectPeopleCount') }}:
          </label>
          <div class="relative mt-2">
            <Icon name="lucide:users" :class="iconClasses" />
            <select
              id="booking-guests"
              v-model="peopleCount"
              :class="inputClasses"
              class="appearance-none cursor-pointer"
            >
              <option class="text-slate-900" disabled :value="null">
                {{ t('roomselector.selectPeopleCount') }}
              </option>
              <option v-for="n in 5" :key="n" class="text-slate-900" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>

        <!-- Submit -->
        <div>
          <button
            type="button"
            :disabled="!canSubmit"
            class="w-full py-3 px-6 h-12 inline-block tracking-widest uppercase font-semibold align-middle duration-500 text-sm text-center bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg cursor-pointer shadow-lg shadow-red-500/30 hover:shadow-red-500/50 transform hover:-translate-y-1 transition-all"
            @click="handleSearch"
          >
            {{ t('search') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Global styles (not scoped) — VueDatePicker renders parts of its UI via
   teleport, so scope boundaries would exclude those elements. The
   `.booking-datepicker` prefix constrains the selectors to this component. */
.booking-datepicker {
  width: 100%;
}
.booking-datepicker > div {
  width: 100%;
}
.booking-datepicker .dp__input {
  width: 100%;
  box-sizing: border-box;
  background-color: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: white;
  height: 3rem;
  padding-left: 2.75rem;
  padding-right: 0.75rem;
  border-radius: 0.5rem;
  /* 16px — prevents iOS Safari zoom-on-focus, which kicks in below 16px. */
  font-size: 1rem;
  transition: all 0.3s;
  backdrop-filter: blur(8px);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}
.booking-datepicker .dp__input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}
.booking-datepicker .dp__input:hover,
.booking-datepicker .dp__input:focus {
  background-color: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.5);
}
.booking-datepicker .dp__input_icon {
  color: rgba(255, 255, 255, 0.85);
  left: 1rem;
}

/* Skeleton button — visible from SSR/FCP until first interaction loads the
   real VueDatePicker. Inherits .dp__input geometry (height, padding, bg,
   border, radius, font-size) from the rule above; we just need to make the
   button render text left-aligned like an input and host the icon absolutely
   so the swap to <input type=text> is visually identical. */
.booking-datepicker .dp__skeleton {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.booking-datepicker .dp__skeleton__icon {
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: rgba(255, 255, 255, 0.85);
  pointer-events: none;
}
.booking-datepicker .dp__skeleton__text {
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.booking-datepicker .dp__skeleton__text.is-placeholder {
  color: rgba(255, 255, 255, 0.7);
}
.booking-datepicker .dp__skeleton--loading {
  opacity: 0.7;
  cursor: progress;
}
</style>
